import React, { useEffect, useRef, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { FiX } from 'react-icons/fi'
import { useTranslation } from '../i18n/LanguageContext'
import './PricingExplanationPopup.css'

function fmt(v) {
    if (v == null || isNaN(v)) return 'N/A'
    return `€${Math.round(v).toLocaleString('nl-NL')}`
}

function PricingExplanationPopup({ isOpen, onClose, car }) {
    const { t } = useTranslation()
    const popupRef = useRef(null)
    const closeButtonRef = useRef(null)

    const TIER_DESCRIPTIONS = {
        tier1: t('Exact Match: Model + Fuel + Transmission + Body + Year ±1 + HP ±10% + Mileage ±15%'),
        tier2: t('Close Match: Model + Fuel + Transmission + Body + Year ±2 + HP ±20% + Mileage ±30%'),
        tier3: t('Relaxed Body: Model + Fuel + Transmission + Year ±3-5 + Mileage ±40%'),
        tier4: t('Fuel Only: Model + Fuel + Year ±3-5 + Mileage ±50%'),
        tier5: t('Model Only: Model + Year ±3-5 + Mileage ±50%'),
        // VAR-8: tier3v matches on the SUBMODEL instead of engine power, for cars whose
        // power output we could not read. Described separately because it is a genuinely
        // different basis, and rendering it as tier3 would tell the user their price came
        // from a power-matched comparison that never happened.
        tier3v: t('Submodel Match: Model + Submodel + Fuel + Transmission + Year ±3-5 + Mileage ±40% (used when engine power is unknown)'),
        continuous: t('Weighted Average: Expanding search with distance-based weighting'),
        NA: t('No Match: Insufficient market data available')
    }

    const ASSUMPTION_LABELS = {
        h: t('Horsepower not specified - included all HP variants'),
        t: t('Transmission not specified - included all transmission types'),
        b: t('Body type not specified - included all body types')
    }

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose()
        }
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown)
            closeButtonRef.current?.focus()
            document.body.style.overflow = 'hidden'
        }
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
            document.body.style.overflow = ''
        }
    }, [isOpen, onClose])

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) onClose()
    }

    // Parse the compact JSON from pricing_explanation_html field
    const pricingData = useMemo(() => {
        if (!car) return null
        const raw = car.pricing_explanation_html
        if (!raw) return null
        // New format: compact JSON like {"wt":85,"wc":70,"wv":80,"cv":12,"a":"ht"}
        if (raw.startsWith('{')) {
            try { return JSON.parse(raw) } catch { return null }
        }
        // Legacy HTML format — return special marker
        return { _legacy: true, html: raw }
    }, [car])

    if (!isOpen || !car) return null

    // Normalize tier to raw format (tier1, tier2, continuous, NA)
    const tier = (() => {
        const raw = car.match_tier_raw || car.match_tier || 'NA'
        // The 'v' suffix must be matched BEFORE the bare-digit patterns below, otherwise
        // 'tier3v' falls through to /Tier\s*(\d)/ and silently renders as tier3.
        if (raw.match(/^tier\d+v$/i)) return raw.toLowerCase()
        if (raw.match(/^tier\d$/i)) return raw.toLowerCase()
        if (raw.toLowerCase() === 'continuous') return 'continuous'
        const m = raw.match(/Tier\s*(\d)/i)
        if (m) return `tier${m[1]}`
        return 'NA'
    })()
    const tierDesc = TIER_DESCRIPTIONS[tier] || t('Unknown')
    const count = Number(car.match_count) || 0
    const price = Number(car.estimated_sales_price_nl) || Number(car.pred_target_price) || null
    const rangeAvailable = car.sale_range_available === true &&
        typeof car.pred_sale_lo === 'number' && Number.isFinite(car.pred_sale_lo) &&
        typeof car.pred_sale_hi === 'number' && Number.isFinite(car.pred_sale_hi) &&
        car.pred_sale_hi > car.pred_sale_lo
    const confidence = Number(car.confidence_score) || Number(car.prediction_confidence_score) || 0
    const downsidePct = rangeAvailable && Number.isFinite(car.sale_range_downside_pct)
        ? car.sale_range_downside_pct : null
    const upsidePct = rangeAvailable && Number.isFinite(car.sale_range_upside_pct)
        ? car.sale_range_upside_pct : null
    const hasData = price != null && !isNaN(price) && price > 0

    // Confidence breakdown from JSON
    const wTier = pricingData?.wt ?? 0
    const wCount = pricingData?.wc ?? 0
    const wCv = pricingData?.wv ?? 0
    const cvPct = pricingData?.cv ?? 0
    const cvLabel = cvPct < 15 ? t('Good') : cvPct < 25 ? t('Fair') : t('High')

    // Assumptions
    const assumptions = (pricingData?.a || '').split('').map(c => ASSUMPTION_LABELS[c]).filter(Boolean)

    return createPortal(
        <div className="pe-popup-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-labelledby="pe-popup-title">
            <div className="pe-popup-container" ref={popupRef}>
                <div className="pe-popup-header">
                    <h3 id="pe-popup-title">
                        {car.make} {car.model} - {t('Price Analysis')}
                    </h3>
                    <button ref={closeButtonRef} className="pe-close-btn" onClick={onClose} aria-label={t('Close popup')}>
                        <FiX />
                    </button>
                </div>

                <div className="pe-popup-content">
                    {pricingData?._legacy ? (
                        <div className="pe-html-content" dangerouslySetInnerHTML={{ __html: pricingData.html }} />
                    ) : hasData ? (
                        <div className="pe-html-content">
                            <div className="pricing-explanation">
                                {/* Match Summary */}
                                <div className="pe-section pe-summary">
                                    <div className="pe-tier">
                                        <span className={`pe-tier-badge tier-${tier}`}>{tier.toUpperCase()}</span>
                                    </div>
                                    <p className="pe-tier-desc">{tierDesc}</p>
                                    <p className="pe-sample">{t('Based on {count} similar cars in Netherlands', { count })}</p>
                                </div>

                                {/* Price Analysis */}
                                <div className="pe-section pe-prices">
                                    <h4>{t('Price Analysis')}</h4>
                                    <div className="pe-row"><span>{t('Predicted Price:')}</span><strong>{fmt(price)}</strong></div>
                                    <div className="pe-row">
                                        <span>{t('Published 80% Price Range:')}</span>
                                        <span>{rangeAvailable ? `${fmt(car.pred_sale_lo)} - ${fmt(car.pred_sale_hi)}` : t('Unavailable')}</span>
                                    </div>
                                </div>

                                {/* BUY-2: how the buy verdict was reached, including the risk we
                                    set aside for an older car. Previously the score appeared with
                                    no derivation, so a rejection was unexplainable and an older
                                    car was silently excluded by an age rule nobody could see. */}
                                {(() => {
                                    const num = (v) => {
                                        const n = typeof v === 'number' ? v : parseFloat(v)
                                        return Number.isFinite(n) ? n : null
                                    }
                                    const ask = num(car.price_amount_eur) ?? num(car.price_amount)
                                    const reserve = num(car.condition_reserve_eur)
                                    const reservePct = num(car.condition_reserve_pct_of_price)
                                    const minMargin = num(car.min_margin_required)
                                    const expErr = num(car.expected_error_pct)
                                    const rev = num(car.expected_revenue)
                                    const marginPct = num(car.expected_margin_pct)
                                    const buyScore = num(car.buy_score)
                                    const age = num(car.year) ? new Date().getFullYear() - num(car.year) : null
                                    // Nothing to explain if none of the decision inputs came through.
                                    if (reserve === null && minMargin === null && rev === null) return null
                                    const netRev = rev !== null && reserve !== null ? rev - reserve : rev
                                    return (
                                        <div className="pe-section pe-buy">
                                            <h4>{t('How the buy verdict was reached')}</h4>
                                            {price && <div className="pe-row"><span>{t('Estimated Dutch value:')}</span><strong>{fmt(price)}</strong></div>}
                                            {ask !== null && <div className="pe-row"><span>{t('Asking price:')}</span><span>{fmt(ask)}</span></div>}
                                            {rev !== null && (
                                                <div className="pe-row"><span>{t('Upside after import costs:')}</span><span>{fmt(rev)}</span></div>
                                            )}
                                            {reserve !== null && reserve > 0 && (
                                                <>
                                                    <div className="pe-row pe-sub">
                                                        <span>{t('Less condition reserve{age}:', { age: age !== null ? t(' ({age} yrs old)', { age }) : '' })}</span>
                                                        <span>-{fmt(reserve)}{reservePct !== null ? ` (${reservePct.toFixed(0)}% ${t('of price')})` : ''}</span>
                                                    </div>
                                                    <div className="pe-row"><span>{t('Upside after reserve:')}</span><strong>{fmt(netRev)}</strong></div>
                                                </>
                                            )}
                                            {expErr !== null && (
                                                <div className="pe-row pe-sub">
                                                    <span>{t('Our own typical error on a car like this:')}</span>
                                                    <span>±{expErr.toFixed(1)}%</span>
                                                </div>
                                            )}
                                            {marginPct !== null && (
                                                <div className="pe-row">
                                                    <span>{t('Margin vs the margin we require:')}</span>
                                                    <span>{(marginPct * 100).toFixed(1)}%
                                                        {minMargin !== null ? ` ${t('vs')} ${(minMargin * 100).toFixed(0)}% ${t('needed')}` : ''}</span>
                                                </div>
                                            )}
                                            {buyScore !== null && (
                                                <div className="pe-row"><span>{t('Buy score:')}</span><strong>{buyScore.toFixed(1)}/100</strong></div>
                                            )}
                                            {car.buy_reason && (
                                                <p className="pe-buy-reason">{car.buy_reason}</p>
                                            )}
                                            <p className="pe-note">
                                                {t('Older cars are not excluded. Instead we set aside a reserve for what a listing cannot show (rust, service history, deferred maintenance), and the margin we require is set by how accurately we can value this particular car: it is 1.5x our own typical error on cars with a price range this wide, measured against 17,710 real Dutch listings. A wide range therefore means a higher bar, not a hidden rejection. A car is only shown as buyable if the upside survives that reserve and clears that bar, and if enough Dutch comparables exist to value it at all. Registration tax (BPM) falls to almost nothing on older cars, which is why they can still be worth importing.')}
                                            </p>
                                        </div>
                                    )
                                })()}

                                {/* Evidence Breakdown */}
                                <div className="pe-section pe-confidence">
                                    <h4>{t('Evidence Breakdown')}</h4>
                                    <div className="pe-row"><span>{t('Evidence score:')}</span><strong>{(confidence * 100).toFixed(1)} / 100</strong></div>
                                    <div className="pe-row pe-sub"><span>{t('Tier Quality:')}</span><span>{wTier}%</span></div>
                                    <div className="pe-row pe-sub"><span>{t('Sample Size Factor:')}</span><span>{wCount}%</span></div>
                                    <div className="pe-row pe-sub"><span>{t('Price Consistency ({label}):', { label: cvLabel })}</span><span>{wCv}%</span></div>
                                </div>

                                <div className="pe-section pe-confidence-doc">
                                    <h4>{t('How to read this score')}</h4>
                                    <p className="pe-doc-lead">
                                        {t('The evidence score ranks how strong and consistent the comparable-car evidence is. It is not the probability that the point estimate is correct. The published price range is the uncertainty statement to use.')}
                                    </p>
                                    <div className="pe-row pe-sub">
                                        <span>{t('Published 80% range around the point:')}</span>
                                        <strong>{downsidePct !== null && upsidePct !== null
                                            ? `-${downsidePct.toFixed(1)}% / +${upsidePct.toFixed(1)}%`
                                            : t('Unavailable')}</strong>
                                    </div>

                                    <h5>{t('How it is calculated')}</h5>
                                    <ul className="pe-doc-list">
                                        <li>{t('We find Dutch cars comparable to this one, in tiers from strict (same model, fuel, gearbox, body, year ±1, power ±10%, mileage ±15%) down to loose (model and year only).')}</li>
                                        <li>{t('The evidence score combines three things: how strict the matching tier was, how many comparable cars were found, and how tightly those cars agree on price.')}</li>
                                    </ul>

                                    <h5>{t('What it does not cover')}</h5>
                                    <ul className="pe-doc-list">
                                        <li>{t('Condition. Dents, rust, service history and accident damage are not visible in listing data, and on an older car they matter more than anything we can measure. A high confidence means the market data is consistent, not that the car is good.')}</li>
                                        <li>{t('We compare against asking prices, not sold prices. Seller optimism and negotiation remain outside the model.')}</li>
                                        <li>{t('Older and rarer cars genuinely have wider ranges. A wide range is the correct answer for a 20-year-old car, not a defect — it is telling you the price truly depends on condition you have to inspect yourself.')}</li>
                                    </ul>

                                    <h5>{t('How to use it')}</h5>
                                    <ul className="pe-doc-list">
                                        <li>{t('Use the low end of the published range, not the point estimate, when deciding whether the margin survives uncertainty.')}</li>
                                        <li>{t('On a weak evidence score, inspect the comparables and the car before committing.')}</li>
                                    </ul>
                                </div>

                                {/* Assumptions */}
                                {assumptions.length > 0 && (
                                    <div className="pe-section pe-assumptions">
                                        <h4>{t('Assumptions Made')}</h4>
                                        {assumptions.map((a, i) => <p key={i} className="pe-assumption">{a}</p>)}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="pe-no-data">
                            <p>{t('No pricing analysis available for this listing.')}</p>
                            <p className="pe-no-data-hint">{t('This may happen if the model is too rare or data is missing.')}</p>
                        </div>
                    )}
                </div>

                <div className="pe-popup-footer">
                    <button className="pe-done-btn" onClick={onClose}>{t('Got it')}</button>
                </div>
            </div>
        </div>,
        document.body
    )
}

export default PricingExplanationPopup
